// Hlasová poznámka (SPEC.md 2.9): nahrávání + rozpoznávání v zařízení, outbox v IndexedDB a nahrání na server.
// Nahrávka se nejdřív uloží do outboxu (přežije offline), pak se nahrává; po nahrání se objeví v chatu Buddyho.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { VOICE_NOTE_MAX_SECONDS } from '@wac-buddy/shared'
import { postVoiceNote } from '@/api/ai'
import { createRecorder, RecorderError, type Recorder } from '@/lib/recorder'
import { createSpeech, type Speech } from '@/lib/speech'
import * as outbox from '@/lib/outbox'
import { useAuthStore } from '@/stores/auth'
import { useBuddyStore } from '@/stores/buddy'

export type VoiceStatus = 'idle' | 'requesting' | 'recording' | 'uploading' | 'error'

/** Kratší nahrávky se zahazují (omylem klepnuté tlačítko). */
export const MIN_DURATION_MS = 1000
/** Za jak dlouho se zkusí znovu odeslat fronta, která selhala na síti nebo serveru. */
export const RETRY_DELAY_MS = 30_000

const EXTENSIONS: Record<string, string> = {
  'audio/webm': 'webm',
  'audio/ogg': 'ogg',
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/x-m4a': 'm4a',
  'audio/aac': 'aac',
}

function buildForm(item: outbox.OutboxItem): FormData {
  const form = new FormData()
  const name = `hlasovka.${EXTENSIONS[item.mime] ?? 'webm'}`
  form.append('audio', new File([item.blob], name, { type: item.mime }), name)
  form.append('noteId', item.id)
  form.append('deviceText', item.deviceText)
  // Pojistka: server odmítne víc než limit, tak ať limit nikdy nepřekročí ani zaokrouhlení.
  form.append(
    'durationMs',
    String(Math.min(Math.round(item.durationMs), VOICE_NOTE_MAX_SECONDS * 1000)),
  )
  if (item.clientId) form.append('clientId', item.clientId)
  if (item.jobId) form.append('jobId', item.jobId)
  return form
}

/** Uživatel a firma, pod kterými se hlasovka nahraje a odešle (null = nikdo není přihlášen). */
function currentOwner(): outbox.OutboxOwner | null {
  const me = useAuthStore().me
  const userId = me?.user.id
  const orgId = me?.org?.id
  return userId && orgId ? { userId, orgId } : null
}

export const useVoiceStore = defineStore('voice', () => {
  const status = ref<VoiceStatus>('idle')
  const elapsedMs = ref(0)
  const level = ref(0)
  const interimText = ref('')
  const finalText = ref('')
  /** Klíč chyby: buddy.voice.errors.<errorKey>. */
  const errorKey = ref<string | null>(null)
  const pendingCount = ref(0)
  /** Poslední hlasovka nahraná z panelu: obrazovka podle ní přejde do chatu. */
  const lastSentNoteId = ref<string | null>(null)

  let recorder: Recorder | null = null
  let speech: Speech | null = null
  /** Zrušení během žádosti o mikrofon: výsledek `start()` se zahodí. */
  let session = 0
  /** Zvyšuje se při změně uživatele nebo firmy: rozdělaný `stop()` pak nic neuloží. */
  let epoch = 0
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  function resetLive(): void {
    elapsedMs.value = 0
    level.value = 0
    interimText.value = ''
    finalText.value = ''
  }

  async function refreshPending(): Promise<void> {
    try {
      pendingCount.value = await outbox.countFor(currentOwner())
    } catch {
      /* úložiště nedostupné */
    }
  }

  function fail(key: string): void {
    errorKey.value = key
    status.value = 'error'
  }

  /** Nahraje jednu položku outboxu a ukáže ji v chatu. */
  async function upload(item: outbox.OutboxItem): Promise<void> {
    const res = await postVoiceNote(buildForm(item))
    useBuddyStore().attachVoiceNote({
      noteId: item.id,
      interactionId: res.interactionId,
      durationMs: item.durationMs,
      deviceText: item.deviceText,
    })
  }

  function scheduleRetry(): void {
    if (retryTimer !== null) return
    retryTimer = setTimeout(() => {
      retryTimer = null
      void flush()
    }, RETRY_DELAY_MS)
  }

  /** Odešle frontu hlasovek (start aplikace, online, po nahrání). Vrací výsledek běhu. */
  async function flush(): Promise<outbox.FlushResult | null> {
    if (retryTimer !== null) {
      clearTimeout(retryTimer)
      retryTimer = null
    }
    let result: outbox.FlushResult
    try {
      result = await outbox.flush({ upload, owner: currentOwner })
    } catch {
      await refreshPending()
      return null
    }
    pendingCount.value = result.remaining
    if (result.remaining > 0) scheduleRetry()
    // Odmítnuté hlasovky už se nezopakují: řekneme to, pokud právě nenahrávám.
    if (result.failed.length > 0 && (status.value === 'idle' || status.value === 'error')) {
      fail('rejected')
    }
    return result
  }

  async function start(): Promise<void> {
    if (status.value !== 'idle' && status.value !== 'error') return
    const mine = ++session
    errorKey.value = null
    resetLive()
    status.value = 'requesting'

    const rec = createRecorder({
      onTick: (ms) => (elapsedMs.value = ms),
      onLevel: (l) => (level.value = l),
      // Limit délky: recorder se zastavil sám, uložíme to stejně jako ručně.
      onLimit: () => void stop(),
    })
    recorder = rec
    try {
      await rec.start()
    } catch (e) {
      if (mine !== session) return
      recorder = null
      fail(e instanceof RecorderError ? e.key : 'unsupported')
      return
    }
    if (mine !== session) {
      // Mezitím přišlo zrušení.
      rec.cancel()
      return
    }

    speech = createSpeech()
    speech.start((final, interim) => {
      finalText.value = final
      interimText.value = interim
    })
    status.value = 'recording'
  }

  /** Zastaví nahrávání, uloží do outboxu a odešle. Vrací noteId, nebo null (zahozeno, chyba). */
  async function stop(): Promise<string | null> {
    if (status.value !== 'recording' || !recorder) return null
    const owner = currentOwner()
    const mine = epoch
    const rec = recorder
    const sp = speech
    recorder = null
    speech = null
    status.value = 'uploading'
    const deviceText = (sp?.stop() ?? '').trim()

    let recording
    try {
      recording = await rec.stop()
    } catch (e) {
      fail(e instanceof RecorderError ? e.key : 'unsupported')
      return null
    }
    // Mezitím se změnil uživatel nebo firma: nahrávka se zahodí, nesmí skončit u cizího účtu.
    if (mine !== epoch) return null
    if (recording.durationMs < MIN_DURATION_MS) {
      fail('tooShort')
      return null
    }

    if (!owner) {
      fail('saveFailed')
      return null
    }

    const noteId = crypto.randomUUID()
    try {
      await outbox.enqueue({
        id: noteId,
        userId: owner.userId,
        orgId: owner.orgId,
        blob: recording.blob,
        mime: recording.mime,
        durationMs: recording.durationMs,
        deviceText,
      })
    } catch {
      fail('saveFailed')
      return null
    }
    await refreshPending()

    const result = await flush()
    if (mine !== epoch) return noteId
    if (result?.uploaded.includes(noteId)) {
      status.value = 'idle'
      lastSentNoteId.value = noteId
      return noteId
    }
    if (result?.failed.some((f) => f.id === noteId)) {
      fail('rejected')
      return null
    }
    // Zůstala ve frontě: offline, přihlášení vypršelo, limit nebo výpadek serveru.
    fail(
      typeof navigator !== 'undefined' && navigator.onLine === false ? 'offlineQueued' : 'queued',
    )
    return noteId
  }

  /** Zahodí rozpracovanou nahrávku (nic se neodešle). */
  function cancel(): void {
    session++
    recorder?.cancel()
    speech?.stop()
    recorder = null
    speech = null
    resetLive()
    errorKey.value = null
    status.value = 'idle'
  }

  /** Zavře panel po chybě nebo po zařazení do fronty. */
  function dismiss(): void {
    if (status.value === 'error') {
      errorKey.value = null
      status.value = 'idle'
    }
  }

  /**
   * Změnil se uživatel nebo firma: zruší nahrávání a vyprázdní stav v paměti. Outbox v IndexedDB
   * zůstává (odešle se jen pod svým vlastníkem), přepočítá se jen čítač čekajících.
   */
  function invalidate(): void {
    epoch++
    cancel()
    lastSentNoteId.value = null
    pendingCount.value = 0
    outbox.clearFailed()
    void refreshPending()
  }

  return {
    status,
    elapsedMs,
    level,
    interimText,
    finalText,
    errorKey,
    pendingCount,
    lastSentNoteId,
    start,
    stop,
    cancel,
    dismiss,
    flush,
    refreshPending,
    invalidate,
  }
})
